import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './careers.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import chrisPhoto from './chris-rush.jpg';

const Hero = () => (
  <section className="about-hero careers-hero">
    <div className="hero-mesh"></div>
    <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1 }}>
      <span className="hero-eyebrow"><span className="dot"></span>Careers at Rush Studio</span>
      <h1 className="about-h1">Help us build the next decade of localization software.</h1>
      <p className="about-lead">We're a small, remote-first team in the UK, building one translation dashboard for the whole localization pipeline — and we're hiring across engineering, design, language operations, and go-to-market.</p>
      <div className="careers-hero-meta">
        <div className="ch-meta"><span className="ch-meta-num">14</span><span className="ch-meta-lbl">open roles</span></div>
        <div className="ch-meta"><span className="ch-meta-num">4</span><span className="ch-meta-lbl">teams hiring</span></div>
        <div className="ch-meta"><span className="ch-meta-num">Remote-first</span><span className="ch-meta-lbl">since day one</span></div>
        <div className="ch-meta"><span className="ch-meta-num">Equity</span><span className="ch-meta-lbl">for every hire</span></div>
      </div>
      <div className="careers-hero-cta">
        <a className="btn btn-accent btn-lg" href="#open-roles">See open roles ↓</a>
        <a className="btn btn-ghost btn-lg" href="#how-we-work">How we work</a>
      </div>
    </div>
  </section>
);

const WHY = [
  { k: '01', title: "You'll work on a thing from the ground up.", body: "You'll help build and ship the features the first wave of teams will ever use. The feedback loop is short, direct, and personal — straight from early users to you." },
  { k: '02', title: 'Small team, large surface area.', body: 'A tiny team where everyone touches the product. Engineers ship their own design. Designers write copy. Everyone talks to early users. Nobody owns just one slice — and nobody is fenced out of one, either.' },
  { k: '03', title: 'We ship every week, calmly.', body: "No hero culture, no all-nighters, no surprise pivots. We've shipped to production every week for 47 of the last 52 weeks. The other 5 were Christmas, Lunar New Year, and Daniel's wedding." },
  { k: '04', title: 'Languages matter here.', body: "Eighteen of us speak more than one language at home. We translate the product into 18 languages ourselves. If you've ever felt that localization is treated as a footnote — you won't feel that way here." },
];

const WhyWorkHere = () => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head-left reveal">
        <div className="eyebrow">Why work here</div>
        <h2 style={{ fontSize: 'clamp(32px, 3.5vw, 44px)', lineHeight: 1.08, letterSpacing: '-0.03em', margin: '0 0 16px' }}>Four honest reasons.</h2>
        <p className="section-lead">We've tried to write these without the recruiting clichés. If anything here feels off when you talk to us, say so.</p>
      </div>
      <div className="why-grid reveal stagger">
        {WHY.map((w) => (
          <article className="why-card" key={w.k}>
            <div className="why-k">{w.k}</div>
            <h3>{w.title}</h3>
            <p>{w.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const HOW = [
  { title: 'Remote-first, three hubs', body: 'London, Berlin, and Lisbon are our gathering points. About half the team lives within commuting distance of one; the other half is fully remote across the EU, UK, US East Coast, Japan, and Brazil. We require an overlap with 10:00–14:00 CET.' },
  { title: 'Async by default', body: 'Two scheduled meetings per week, max, for most roles: a Monday kickoff (45 min) and a Friday demo (30 min). Everything else is a written doc, a Loom, or a thread. We hold pairing time and 1:1s as opt-in.' },
  { title: 'Ship every week', body: "Every team ships something visible to customers every week. Not a sprint, not a stretch — a default. If you can't ship this week, that's a flag we work on together, not a reason to push harder." },
  { title: 'We meet in person twice a year', body: 'One off-site in Lisbon every spring (one week, optional family-friendly). One smaller team-level retreat in autumn. Travel and lodging covered, including from anywhere remote.' },
  { title: 'Six-hour Fridays', body: 'Standing rule: Friday afternoons are no-meetings, no-Slack-expected, and end at 16:00 local. We use the time to write, read, learn, or stop.' },
  { title: 'Open salary bands', body: 'Every role has a published band, every band is the same for the same level worldwide (adjusted only for tax and cost-of-living, not for negotiating skill). New hires start mid-band by default.' },
];

const HowWeWork = () => (
  <section className="section" id="how-we-work" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">How we work</div>
        <h2>Calm software, on purpose.</h2>
        <p className="section-lead">A few of the operating rules that make this a place people stay. Most of these we figured out the hard way.</p>
      </div>
      <div className="how-grid reveal stagger">
        {HOW.map((h) => (<article className="how-card" key={h.title}><h3>{h.title}</h3><p>{h.body}</p></article>))}
      </div>
    </div>
  </section>
);

const BENEFITS = [
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v5c0 5-3.4 8-8 9.5C7.4 19 4 16 4 11V6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M9.2 11.8l2 2 3.6-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>), title: 'Pension, matched', body: 'Workplace pension with employer contributions matched up to 5% — well above auto-enrolment minimums, from day one.' },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 7l9-4 9 4-9 4-9-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M3 12l9 4 9-4M3 17l9 4 9-4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>), title: 'Real equity', body: 'EMI share options for every hire, with a 10-year exercise window. We share the cap table with the team twice a year.' },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.8" /><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>), title: '25 days + bank holidays', body: "25 days' annual leave on top of all UK bank holidays. We genuinely expect you to take it, and we'll nudge you if you don't." },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 20s-7-4.3-7-9.5A4 4 0 0112 7a4 4 0 017 3.5C19 15.7 12 20 12 20z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>), title: 'Private health cover', body: 'Private medical insurance including dental and optical, plus mental-health and therapy support — no cap, no questions.' },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 11l8-7 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M6 9.5V20h12V9.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M10 20v-5h4v5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>), title: 'Remote-first', body: 'Work from anywhere in the UK. Drop into our London space when you want a desk, or stay fully remote — we hire for output, not hours at a desk.' },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 6h16v12H4z M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M8 14h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>), title: '£1,000 home-office budget', body: 'Renewable every two years on top of your laptop and kit. Desk, chair, monitor — whatever makes working from home comfortable.' },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 19V5l8 5 8-5v14" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>), title: '£1,500 / year to learn', body: "Books, courses, conferences, language lessons. Plus 5 paid days a year for learning that isn't tied to a project." },
  { icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 12l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M11 12l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" /></svg>), title: 'Enhanced parental leave', body: 'Well above UK statutory: six months\' full pay for the primary caregiver, three for the secondary, with a phased, flexible return.' },
];

const Benefits = () => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">What we offer</div>
        <h2>The benefits, on one page.</h2>
        <p className="section-lead">Remote-first across the UK. The things we think every team should offer as standard — plus a few we're proud of.</p>
      </div>
      <div className="benefit-grid reveal stagger">
        {BENEFITS.map((b) => (<article className="benefit-card" key={b.title}><div className="benefit-icon">{b.icon}</div><h3>{b.title}</h3><p>{b.body}</p></article>))}
      </div>
    </div>
  </section>
);

const ROLES = [
  { dept: 'Engineering', title: 'Senior Product Engineer', loc: 'London · Remote-friendly', type: 'Full-time', band: '€140–180k', desc: 'TypeScript, Postgres, the whole stack. Owns one of: editor, projects, integrations.' },
  { dept: 'Engineering', title: 'Senior Backend Engineer', loc: 'Remote (EU)', type: 'Full-time', band: '€140–180k', desc: 'Distributed systems, queues, the translation memory engine. Loves a hard correctness problem.' },
  { dept: 'Engineering', title: 'Localization Engineer', loc: 'Berlin · Remote-friendly', type: 'Full-time', band: '€120–160k', desc: 'File-format wrangler. XLIFF, ICU MessageFormat, every CMS export quirk. Ours is the canonical version.' },
  { dept: 'Engineering', title: 'Senior Platform Engineer', loc: 'Remote (EU)', type: 'Full-time', band: '€140–180k', desc: 'AWS, Terraform, observability. Owns the runtime our SOC 2 auditors keep asking about.' },
  { dept: 'Engineering', title: 'Engineering Manager, Editor', loc: 'London / Lisbon', type: 'Full-time', band: '€160–200k', desc: 'Lead the team behind the side-by-side editor. Player-coach, 4 reports.' },
  { dept: 'Design', title: 'Senior Designer, Editor', loc: 'Lisbon · Remote', type: 'Full-time', band: '€110–150k', desc: 'The CAT editor — keyboard-first, density-conscious, ultra-detailed. Bring a portfolio of complex tools.' },
  { dept: 'Design', title: 'Product Designer, Workflows', loc: 'Remote (EU)', type: 'Full-time', band: '€100–140k', desc: 'Project setup, reviewer flows, glossary tools. Visual and systems-thinking, equal parts.' },
  { dept: 'Design', title: 'Brand & Marketing Designer', loc: 'Remote (EU / UK)', type: 'Full-time', band: '€90–125k', desc: 'Owns the marketing site, illustration system, and conference presence. Loves a typographic challenge.' },
  { dept: 'Language Ops', title: 'JP–EN Reviewer Lead', loc: 'Tokyo', type: 'Full-time', band: '¥12–16M', desc: 'Native JP, professional EN. Picks and supervises freelance reviewers for our JP customers.' },
  { dept: 'Language Ops', title: 'Localization Quality Manager', loc: 'Remote (EU)', type: 'Full-time', band: '€95–125k', desc: 'Define QA processes, sample reviews, run the linguist-of-the-month program. Three languages minimum.' },
  { dept: 'Language Ops', title: 'Freelance Translator Network Manager', loc: 'Remote (any)', type: 'Full-time', band: '€90–120k', desc: 'Recruit, vet, and grow our network of 400+ freelance translators. Vendor management at heart.' },
  { dept: 'Go-to-market', title: 'Account Executive, EMEA', loc: 'London', type: 'Full-time', band: '€110–140k OTE', desc: "Mid-market deals. ICP is heads of localization at €100M+ businesses. You've sold tools like ours before." },
  { dept: 'Go-to-market', title: 'Customer Success Engineer', loc: 'New York · Remote', type: 'Full-time', band: '$130–170k', desc: 'Owns onboarding for our top 50 accounts. Half technical, half human.' },
  { dept: 'Go-to-market', title: 'Content Marketer', loc: 'Remote (EU / UK)', type: 'Full-time', band: '€85–115k', desc: 'Owns the blog, the Languages page deep dives, customer case studies. Strong opinions on em-dashes.' },
];

const DEPTS = ['All', 'Engineering', 'Design', 'Language Ops', 'Go-to-market'];
const LOCS = ['All locations', 'Remote-friendly', 'London', 'Berlin', 'Lisbon', 'Tokyo', 'New York'];

const OpenRoles = () => {
  const [dept, setDept] = useState('All');
  const [loc, setLoc] = useState('All locations');

  const filtered = ROLES.filter((r) => {
    if (dept !== 'All' && r.dept !== dept) return false;
    if (loc !== 'All locations') {
      if (loc === 'Remote-friendly') return r.loc.toLowerCase().includes('remote');
      return r.loc.toLowerCase().includes(loc.toLowerCase());
    }
    return true;
  });

  const byDept = {};
  filtered.forEach((r) => { (byDept[r.dept] = byDept[r.dept] || []).push(r); });

  return (
    <section className="section" id="open-roles" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">Open roles</div>
          <h2>{ROLES.length} positions, four teams.</h2>
          <p className="section-lead">If you don't see your exact role, write to us anyway. We track future hires for at least 12 months.</p>
        </div>

        <div className="roles-filters reveal">
          <div className="filter-group">
            <span className="filter-label">Team</span>
            <div className="filter-pills">
              {DEPTS.map((d) => (
                <button key={d} className={`filter-pill ${dept === d ? 'is-active' : ''}`} onClick={() => setDept(d)}>
                  {d}
                  <span className="filter-pill-count">{d === 'All' ? ROLES.length : ROLES.filter((r) => r.dept === d).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <span className="filter-label">Location</span>
            <select className="filter-select" value={loc} onChange={(e) => setLoc(e.target.value)}>
              {LOCS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="roles-list reveal">
          {Object.keys(byDept).length === 0 && (
            <div className="roles-empty">
              <div className="roles-empty-mark">·</div>
              <h4>Nothing open in that exact slice right now.</h4>
              <p>Drop us a note at <a href="mailto:careers@rushstudio.com">careers@rushstudio.com</a> — we track future hires for 12 months and we'll reach out when something opens.</p>
            </div>
          )}
          {Object.entries(byDept).map(([d, list]) => (
            <div className="roles-dept" key={d}>
              <div className="roles-dept-head">
                <h3>{d}</h3>
                <span>{list.length} {list.length === 1 ? 'role' : 'roles'}</span>
              </div>
              <div className="roles-dept-list">
                {list.map((r) => (
                  <a className="role-card" href={`mailto:careers@rushstudio.com?subject=${encodeURIComponent('Application: ' + r.title)}`} key={r.title}>
                    <div className="role-card-main">
                      <h4>{r.title}</h4>
                      <p>{r.desc}</p>
                      <div className="role-card-meta">
                        <span className="role-tag">{r.loc}</span>
                        <span className="role-tag">{r.type}</span>
                        <span className="role-tag role-band">{r.band}</span>
                      </div>
                    </div>
                    <div className="role-card-arrow" aria-hidden="true">→</div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const STEPS = [
  { k: '01', title: 'First call — 30 minutes', desc: "With our recruiter Mara. Not a screening test; a two-way conversation about whether we're worth your time." },
  { k: '02', title: 'Hiring manager — 45 minutes', desc: 'Talk through your past work, the role, what would make it a great fit. You get to ask the hard questions back.' },
  { k: '03', title: 'Practical exercise — async', desc: 'A short, take-home task that mirrors actual work. We cap it at 4 hours and pay you for your time. You keep what you produce.' },
  { k: '04', title: 'Team conversations — 2× 45 min', desc: 'Two video calls with future teammates. One focused on craft, one on collaboration. We share the questions in advance.' },
  { k: '05', title: 'Founder chat — 30 minutes', desc: 'With Chris. Mostly about what would make Rush Studio the best decision you make this year — for both sides.' },
  { k: '06', title: 'References, offer, hello', desc: 'We reach out to two of your past collaborators. Offer goes out within five working days of the last conversation.' },
];

const Process = () => (
  <section className="section" id="process">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">Interview process</div>
        <h2>Six steps, three to four weeks.</h2>
        <p className="section-lead">Same process for every role. No surprise rounds, no panel ambushes, no take-home tasks pretending to be evaluations of "passion."</p>
      </div>
      <div className="process-timeline reveal stagger">
        {STEPS.map((s, i) => (
          <div className="process-row" key={s.k}>
            <div className="process-k">{s.k}</div>
            <div className="process-marker">
              <span className="process-dot"></span>
              {i < STEPS.length - 1 && <span className="process-line"></span>}
            </div>
            <div className="process-body"><h4>{s.title}</h4><p>{s.desc}</p></div>
          </div>
        ))}
      </div>
      <div className="process-promises reveal">
        <div className="process-promise"><span className="pp-num">No</span><span className="pp-lbl">whiteboards or trick questions</span></div>
        <div className="process-promise"><span className="pp-num">5 days</span><span className="pp-lbl">from last interview to offer</span></div>
        <div className="process-promise"><span className="pp-num">Always</span><span className="pp-lbl">a written response, even when it's a no</span></div>
      </div>
    </div>
  </section>
);

const FounderNote = () => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="founder-note reveal">
        <div className="founder-note-mark">A note from us</div>
        <p className="founder-note-lede">We spent fifteen years watching localization tools treat translators as a cost line and PMs as an interruption.</p>
        <p>Rush Studio is our attempt to build the opposite. If that resonates — whether you ship code, ship copy, ship deals, or ship words from one language to another — we'd love to talk. The page above lists the formal roles. The list below is everything we wish we'd have known before joining a startup.</p>
        <div className="founder-note-list">
          <div><strong>You will not be the smartest person in the room.</strong> You will also not be the dumbest. The point is the room.</div>
          <div><strong>The product is unfinished.</strong> It is meant to be. The interesting work is what's missing.</div>
          <div><strong>The thing you're best at probably isn't your job title.</strong> We'll find it.</div>
        </div>
        <div className="founder-note-sig">
          <div className="founder-sig-row">
            <div className="founder-sig-av" style={{ overflow: 'hidden', padding: 0 }}>
              <img src={chrisPhoto} alt="Chris Rush" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
            </div>
            <div><b>Chris Rush</b><span>Founder</span></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="section">
    <div className="rs-container">
      <div className="careers-final reveal reveal-scale">
        <h2>Don't see your exact role?</h2>
        <p>Write to us anyway. We answer everyone, we keep notes, and we reach out when something opens.</p>
        <div className="careers-final-row">
          <a className="btn btn-accent btn-lg" href="mailto:careers@rushstudio.com">careers@rushstudio.com</a>
          <Link className="btn btn-ghost btn-lg" to="/about">Read about the team →</Link>
        </div>
      </div>
    </div>
  </section>
);

const RushCareersPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="careers" />
      <Hero />
      <WhyWorkHere />
      <HowWeWork />
      <Benefits />
      <OpenRoles />
      <Process />
      <FounderNote />
      <FinalCTA />
      <RushFooter />
    </div>
  );
};

export default RushCareersPage;
